import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { GuidService } from 'src/app/shared/util/guid.service';
import { FormV2ElementEditComponent } from '../form-v2-element-edit/form-v2-element-edit.component';
import { FormV2ElementTypeEnum } from '../shared/dto/form-v2-element-type.enum';
import { FormV2ElementDto } from '../shared/dto/form-v2-element.dto';
import { FormV2TemplateDto } from '../shared/dto/form-v2-template.dto';
import { FormBuilderDto } from '../shared/dto/form-builder.dto';
import { FormSettingsPaymentOptionsDto } from '../shared/dto/form-settings-payment-options.dto';
import { FormSettingsDto } from '../shared/dto/form-settings.dto';
import { FormV2TypeEnum } from '../shared/enum/form-v2-type.enum';
import { FormBuilderService } from '../shared/service/form-builder.service';
import { cloneDeep } from 'lodash-es';
import { AuthService, NotificationService } from 'src/app/shared';
import { DonationDefaultTemplate } from '../shared/dto/renewal-form-default-template.dto';
import { Title } from '@angular/platform-browser';
import { FormBuilderUtils } from '../shared/service/form-builder.utils';

@Component({
  selector: 'wl-donation-form-edit',
  templateUrl: './donation-form-edit.component.html',
  styleUrls: ['./donation-form-edit.component.less'],
    standalone: false
})
export class DonationFormEditComponent implements OnInit, OnDestroy {
  @ViewChild(FormV2ElementEditComponent) _donationFormEdit: FormV2ElementEditComponent;
  @Output() formSaved = new EventEmitter<number>();

  private _onDestroy$ = new Subject<void>();
  formSettings: FormSettingsDto;
  showSettings = false;
  donationFormTemplate: FormV2TemplateDto;
  saveIndex = -1;
  elementType = FormV2ElementTypeEnum;
  formType = FormV2TypeEnum;
  editId = 0;
  tenantHttpsUrl = '';
  formViewerUrl = '';
  inactive = false;

  constructor(
    private _guidService: GuidService,
    private _formBuilderService: FormBuilderService,
    private _route: ActivatedRoute,
    private _notificationService: NotificationService,
    private _authService: AuthService,
    private _title: Title
  ) {
    this.formSettings = { LoginRequired: true, FormPaymentOptions: {} as FormSettingsPaymentOptionsDto } as FormSettingsDto;
    this.donationFormTemplate = { FormElements: [], FormSettings: this.formSettings } as FormV2TemplateDto;
  }

  ngOnInit(): void {
    this.tenantHttpsUrl = this._authService.getTenantHttpsUrl();
    this._route.params
      ?.pipe(
        map((params) => {
          if (params.id) {
            const formId = parseInt(params.id, 10);
            this.editId = formId;
            this._formBuilderService.get(formId).then((data) => {
              let formTemplate: FormV2TemplateDto = JSON.parse(data.Dto);
              this.inactive = data.Inactive;
              this.formSettings = formTemplate.FormSettings;
              this.donationFormTemplate = formTemplate;
              this.formViewerUrl = `${this.tenantHttpsUrl}/atlas/forms/donation/${this.editId}`;

              if (this.donationFormTemplate.Title) {
                this._title.setTitle(this.donationFormTemplate.Title + ' ' + this._title.getTitle());
              }
            });
          } else {
            this.editId = 0;
            this.donationFormTemplate = DonationDefaultTemplate;
          }
        }, takeUntil(this._onDestroy$))
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._onDestroy$.next(null);
  }

  cancelSettings() {
    this.showSettings = false;
  }

  openSettings() {
    if (this.donationFormTemplate.FormSettings) {
      this.formSettings = cloneDeep(this.donationFormTemplate.FormSettings);
    }

    this.showSettings = true;
  }

  saveSettings() {
    this.donationFormTemplate.FormSettings = cloneDeep(this.formSettings);
    this.showSettings = false;
  }

  addFormElementClicked() {
    let formElement = { ElementType: FormV2ElementTypeEnum.Header } as FormV2ElementDto;
    this._donationFormEdit.open(formElement);
    this.saveIndex = -1;
  }

  insertFormElementClicked(index: number) {
    let formElement = { ElementType: FormV2ElementTypeEnum.Header } as FormV2ElementDto;
    this._donationFormEdit.open(formElement);
    this.saveIndex = index;
  }

  editFormElementClicked(formElement: FormV2ElementDto) {
    this._donationFormEdit.open(formElement);
  }

  elementSaveClicked(formElement: FormV2ElementDto) {
    if (!formElement.Id) {
      const valid = FormBuilderUtils.validateElementToAdd(formElement, this.donationFormTemplate.FormElements)

      if (!valid) {
        this._notificationService.translateError('forms.form-builder.only-one-element-is-allowed');
        return;
      }

      this.pushElement(formElement);
      return;
    }

    // replace the old element with the new
    let element = this.donationFormTemplate.FormElements.find((x) => x.Id == formElement.Id);
    let index = this.donationFormTemplate.FormElements.indexOf(element);
    this.donationFormTemplate.FormElements.splice(index, 1, formElement);
  }

  removeClicked(index: number) {
    this.donationFormTemplate.FormElements.splice(index, 1);
  }

  moveUp(index: number) {
    const formElement = this.donationFormTemplate.FormElements[index];
    if (index > 0) {
      this.donationFormTemplate.FormElements.splice(index, 1);
      index--;
      this.donationFormTemplate.FormElements.splice(index, 0, formElement);
    }
  }

  moveDown(index: number) {
    const formElement = this.donationFormTemplate.FormElements[index];
    if (index < this.donationFormTemplate.FormElements.length) {
      this.donationFormTemplate.FormElements.splice(index, 1);
      this.donationFormTemplate.FormElements.splice(index + 1, 0, formElement);
    }
  }

  private pushElement(formElement: FormV2ElementDto) {
    formElement.Id = this._guidService.getNewShortGuid();
    if (this.saveIndex > -1) {
      this.donationFormTemplate.FormElements.splice(this.saveIndex, 0, formElement);
    } else {
      this.donationFormTemplate.FormElements.push(formElement);
    }
    this.saveIndex = -1;
  }

  async saveClicked() {
    let json = JSON.stringify(this.donationFormTemplate);
    let formBuilder = { Id: this.editId, Dto: json, FormType: FormV2TypeEnum.Donation, Inactive: this.inactive } as FormBuilderDto;
    try {
      const formId = await this._formBuilderService.saveForm(formBuilder);
      this._notificationService.translateSuccess('form-builder.form-saved');
      this.formSaved.emit(formId);
    } catch (err) {
      this._notificationService.httpError(err);
    }
  }

  private toggleActive() {
    this.inactive = !this.inactive;
  }
}
